import axiosClient from '../axiosClient';
import { AnalyticsEndpoints } from '../endpoints/analytics';
import {
  IAnalyticsViewMeta,
  IAnalyticsViewResponse,
  IPipelineRunStatus,
  IPipelineTriggerResponse,
} from '../../types/api/response/analyticsResponse';

export interface IAnalyticsQuery {
  /** column -> value(s); comma-join for IN-lists. Renders as filter[column]=... */
  filters?: Record<string, string>;
  /** e.g. 'sort_order,display_name' or '-bulk_total' */
  sort?: string;
  limit?: number;
  offset?: number;
}

const buildParams = (query?: IAnalyticsQuery): URLSearchParams => {
  const params = new URLSearchParams();
  if (query?.filters) {
    Object.entries(query.filters).forEach(([column, value]) => {
      params.append(`filter[${column}]`, value);
    });
  }
  if (query?.sort) params.append('sort', query.sort);
  if (query?.limit !== undefined) params.append('limit', String(query.limit));
  if (query?.offset !== undefined) params.append('offset', String(query.offset));
  return params;
};

/** Cached: the catalog is static per deployment of the analytics service. */
let catalogPromise: Promise<IAnalyticsViewMeta[]> | null = null;

export const AnalyticsService = {
  async getCatalog(): Promise<IAnalyticsViewMeta[]> {
    if (!catalogPromise) {
      catalogPromise = axiosClient
        .get<IAnalyticsViewMeta[]>(AnalyticsEndpoints.GET_CATALOG)
        .then((res) => res.data)
        .catch((e) => {
          catalogPromise = null; // don't cache failures
          throw e;
        });
    }
    return catalogPromise;
  },

  async queryView(view: string, query?: IAnalyticsQuery): Promise<IAnalyticsViewResponse> {
    const res = await axiosClient.get<IAnalyticsViewResponse>(AnalyticsEndpoints.QUERY_VIEW(view), {
      params: buildParams(query),
    });
    return res.data;
  },

  /**
   * Fires the composite nightly job (products -> stock -> models) on demand.
   *
   * A 409 is NOT a failure. The pipeline is single-flight, so when a sync is already in
   * progress the service refuses to start a second one and hands back the run id of the one
   * that is running — which satisfies what the caller actually asked for ("make sure the data
   * is refreshing"). Translating that into a rejected promise made the caller report a broken
   * button for a working system. Anything else propagates.
   */
  async triggerRefresh(): Promise<IPipelineTriggerResponse> {
    try {
      const res = await axiosClient.post<IPipelineTriggerResponse>(
        AnalyticsEndpoints.TRIGGER_PIPELINE,
      );
      return res.data;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      const body = e?.response?.data;
      if (
        e?.response?.status === 409 &&
        body?.status === 'ALREADY_RUNNING' &&
        typeof body?.runId === 'number'
      ) {
        return body as IPipelineTriggerResponse;
      }
      throw e;
    }
  },

  async getRunStatus(runId: number): Promise<IPipelineRunStatus> {
    const res = await axiosClient.get<IPipelineRunStatus>(AnalyticsEndpoints.GET_RUN_STATUS(runId));
    return res.data;
  },
};
